import type { GraphQLPage } from '~/types/graphql';
import type { SiteOption, StrapiResponse } from '~/types/strapi';

export default defineNuxtRouteMiddleware(async to => {
  const { setTheme } = useThemeState();
  // Get locale from route - i18n strategy is 'prefix_except_default' with default 'de'
  // For default locale, to.params.locale might not exist, so default to 'de'
  const locale = (to.params.locale as string) || 'de';
  const { data: siteOptions } = await useSiteOptions(locale);

  // Check if page has cmsConfig
  // In Nuxt, definePageMeta should be available in to.meta, but if it's not,
  // we can use route-based fallback logic
  let cmsConfig = (to.meta as any).cmsConfig;

  // Fallback: if meta is empty, try to infer from route path/name
  if (!cmsConfig) {
    return;
  }

  let targetSlug: string | null = null;

  // Resolve slug based on strategy
  switch (cmsConfig.strategy) {
    case 'static':
      targetSlug = cmsConfig.slug || null;
      break;

    case 'params': {
      const paramKey = cmsConfig.paramKey || 'slug';
      const paramValue = to.params[paramKey];
      if (paramValue) {
        const slug = Array.isArray(paramValue) ? paramValue[0] : (paramValue as string);
        targetSlug = slug || null;
      }
      break;
    }

    case 'query': {
      const queryKey = cmsConfig.queryKey || 'slug';
      const queryValue = to.query[queryKey];
      if (queryValue) {
        const slug = Array.isArray(queryValue) ? queryValue[0] : (queryValue as string);
        targetSlug = slug || null;
      }
      break;
    }

    case 'custom':
      if (cmsConfig.siteOptionKey) {
        // Fetch siteOptions directly - can't use useSiteOptions in middleware
        const siteOptions = await $fetch<StrapiResponse<SiteOption>>('/api/site-options', {
          query: {
            locale,
          },
        });

        if (siteOptions?.data) {
          // Resolve slug from siteOptions using dot notation path
          const keys = cmsConfig.siteOptionKey.split('.');
          let value: any = siteOptions.data;

          for (const key of keys) {
            value = value?.[key];
            if (value === undefined || value === null) {
              break;
            }
          }

          if (typeof value === 'string') {
            targetSlug = value;
          }
        }
      }
      break;
  }

  // Resolve actual slug for homepage case (like pages do)
  let actualSlug = targetSlug;

  // If no slug, fetch homepage slug from siteOptions
  if (!targetSlug) {
    // Fetch siteOptions directly - can't use useSiteOptions in middleware
    actualSlug = siteOptions.value?.data?.homepage?.slug || '';
    if (!actualSlug) {
      return;
    }
  }

  // Use useGetPage composable - it handles caching with the correct key format
  // This ensures the middleware and page component share the same cache
  let pageData: GraphQLPage | null = null;

  try {
    const { data } = await useGetPage(actualSlug || '', locale);
    pageData = data.value || null;
  } catch (error: any) {
    // Page not found - page component will handle 404s appropriately
    if (error.statusCode === 404) {
      return;
    }
    return;
  }

  if (pageData) {
    setTheme(pageData.invertedHeader ? 'dark' : 'light');
  }
});
